import React from 'react';
import { Box, Typography } from '@mui/material';
import FormField from './FormField';
import { ChildRow } from './ChildRow';
import { colors, formUi } from '../constants/globalcss';
import { ILabel } from '../constants/interfaces';
import { IFormStateValue } from '../utils/formUtils';
import { OTHER_LINE_ITEM_DESCRIPTION_LABEL_ID } from '../constants/app';
import {
  Row,
  emptyRow,
  isOtherLineItem,
  rowsToChildren,
  sumRowValues,
  formatRowForDisplay,
  getCustomFieldConfigsFromChild,
  valueKeyForCustomField,
  READABLE_PLACEHOLDER,
  tableFieldOutlineSx,
  totalFieldWidth,
} from './childRowUtils';

declare global {
  interface Window {
    summationValidation?: Record<number, boolean>;
    summationValidationTriggered?: boolean;
  }
}

interface ChildRowListProps {
  field: ILabel;
  value?: string;
  onChange: (
    value: string,
    children?: { labelId: number; response: string }[][],
    labelId?: number
  ) => void;
  formStateForVisibility?: Record<string, IFormStateValue>;
  onAuxiliaryFieldChange?: (labelId: number, value: string) => void;
  errorText?: string;
  isLabelHidden?: boolean;
  displayOnly?: boolean;
  isInGrid?: boolean;
  resetTrigger?: number;
  width?: string;
  hasTableField?: boolean;
}

function RequiredMark({ show }: { show: boolean }) {
  if (!show) return null;
  return (
    <Typography component="span" sx={{ color: colors.error, ml: 0.25 }}>
      *
    </Typography>
  );
}

const ChildRowList: React.FC<ChildRowListProps> = ({
  field,
  onChange,
  formStateForVisibility,
  onAuxiliaryFieldChange,
  resetTrigger,
  displayOnly,
  errorText,
  isLabelHidden = false,
}) => {
  const [rows, setRows] = React.useState<Row[]>([emptyRow()]);

  const valueFieldTemplate = React.useMemo(
    (): ILabel => ({
      formula: null,
      isMandatory: field.isMandatory,
      labelId: field.labelId,
      options: [],
      orderIndex: field.orderIndex,
      rules: null,
      text: 'Amount',
      type: 'currency',
    }),
    [field]
  );

  const lastEmittedRef = React.useRef<{
    value: string;
    children: { labelId: number; response: string }[][];
  } | null>(null);

  React.useEffect(() => {
    setRows([emptyRow()]);
    lastEmittedRef.current = null;
  }, [resetTrigger, field.labelId]);

  const formattedSum = React.useMemo(() => sumRowValues(rows), [rows]);

  React.useEffect(() => {
    const incomplete = rows.some(
      (r) =>
        r.selectedText &&
        (!r.neighborComplaint.trim() ||
          !r.codeIssue.trim() ||
          !r.preservationOrCosmetic.trim() ||
          !r.isEmergency.trim() ||
          !r.willPicturesAttached.trim() ||
          (field.isMandatory && !r.response.trim()) ||
          (isOtherLineItem(r.selectedText) && !r.otherDescription.trim()))
    );
    const hasErrors = incomplete || parseFloat(formattedSum) === 0;
    if (typeof window !== 'undefined') {
      window.summationValidation = window.summationValidation || {};
      window.summationValidation[field.labelId] = hasErrors;
    }
    return () => {
      if (typeof window !== 'undefined') {
        window.summationValidation = window.summationValidation || {};
        delete window.summationValidation[field.labelId];
      }
    };
  }, [rows, formattedSum, field.labelId, field.isMandatory]);

  React.useEffect(() => {
    const childrenPayload = rowsToChildren(
      rows,
      field.children,
      formStateForVisibility
    );
    const last = lastEmittedRef.current;
    if (
      last &&
      last.value === formattedSum &&
      JSON.stringify(last.children) === JSON.stringify(childrenPayload)
    ) {
      return;
    }
    lastEmittedRef.current = { value: formattedSum, children: childrenPayload };
    onChange(formattedSum, childrenPayload);
  }, [rows, field.children, formStateForVisibility, onChange, formattedSum]);

  React.useEffect(() => {
    if (!onAuxiliaryFieldChange || displayOnly) return;
    const text = rows
      .filter((r) => r.selectedText && isOtherLineItem(r.selectedText))
      .map((r) => r.otherDescription.trim())
      .filter(Boolean)
      .join('\n');
    onAuxiliaryFieldChange(OTHER_LINE_ITEM_DESCRIPTION_LABEL_ID, text);
  }, [rows, onAuxiliaryFieldChange, displayOnly]);

  // ─── Handlers ───────────────────────────────────────────────────────────────

  const handleSelectChange = (rowId: string, newText: string) => {
    setRows((prev) => {
      const idx = prev.findIndex((r) => r.id === rowId);
      if (idx < 0) return prev;
      const row = prev[idx];
      const childDef = field.children?.find((c) => c.text === newText);
      const customFields = newText
        ? getCustomFieldConfigsFromChild(childDef)
        : [];
      const customValues: Record<string, string> = {};
      customFields.forEach((cf, i) => {
        customValues[valueKeyForCustomField(cf, i)] = '';
      });
      const next = prev.map((r) =>
        r.id === rowId
          ? {
              ...r,
              customFields,
              customValues,
              otherDescription:
                newText &&
                isOtherLineItem(newText) &&
                isOtherLineItem(r.selectedText) &&
                r.selectedText === newText
                  ? r.otherDescription
                  : '',
              selectedText: newText,
              response: newText ? r.response : '',
              neighborComplaint: newText ? r.neighborComplaint : '',
              codeIssue: newText ? r.codeIssue : '',
              preservationOrCosmetic: newText ? r.preservationOrCosmetic : '',
              isEmergency: newText ? r.isEmergency : '',
              willPicturesAttached: newText ? r.willPicturesAttached : '',
            }
          : r
      );
      const isLast = idx === prev.length - 1;
      if (isLast && !row.selectedText && newText) return [...next, emptyRow()];
      return next;
    });
  };

  const handleValueChange = (rowId: string, v: string) =>
    setRows((prev) =>
      prev.map((r) => (r.id === rowId ? { ...r, response: v } : r))
    );

  const handleOtherDescriptionChange = (rowId: string, v: string) =>
    setRows((prev) =>
      prev.map((r) => (r.id === rowId ? { ...r, otherDescription: v } : r))
    );

  const handleNeighborComplaintChange = (rowId: string, v: string) =>
    setRows((prev) =>
      prev.map((r) => (r.id === rowId ? { ...r, neighborComplaint: v } : r))
    );

  const handleCodeIssueChange = (rowId: string, v: string) =>
    setRows((prev) =>
      prev.map((r) => (r.id === rowId ? { ...r, codeIssue: v } : r))
    );

  const handlePreservationOrCosmeticChange = (rowId: string, v: string) =>
    setRows((prev) =>
      prev.map((r) =>
        r.id === rowId ? { ...r, preservationOrCosmetic: v } : r
      )
    );

  const handleEmergencyChange = (rowId: string, v: string) =>
    setRows((prev) =>
      prev.map((r) => (r.id === rowId ? { ...r, isEmergency: v } : r))
    );

  const handlePicturesAttachedChange = (rowId: string, v: string) =>
    setRows((prev) =>
      prev.map((r) => (r.id === rowId ? { ...r, willPicturesAttached: v } : r))
    );

  const handleDeleteRow = (rowId: string) => {
    setRows((prev) => {
      let next = prev.filter((r) => r.id !== rowId);
      if (next.length === 0) return [emptyRow()];
      if (next[next.length - 1].selectedText) next = [...next, emptyRow()];
      return next;
    });
  };

  // ─── Shared total field definition ───────────────────────────────────────────

  const totalFieldBase: ILabel = {
    formula: null,
    isMandatory: field.isMandatory,
    labelId: field.labelId,
    options: [],
    orderIndex: 9999,
    rules: null,
    text: 'Total',
    type: 'currency',
  };

  // ─── Display-only render ─────────────────────────────────────────────────────

  if (displayOnly) {
    const filled = rows.filter((r) => r.selectedText);
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: filled.length ? 12 : 8,
        }}
      >
        {filled.length === 0 ? (
          <Typography
            component="span"
            sx={{ color: READABLE_PLACEHOLDER }}
            variant="body2"
          >
            —
          </Typography>
        ) : (
          filled.map((r) => (
            <div key={r.id}>
              {formatRowForDisplay(r, field, formStateForVisibility)}
            </div>
          ))
        )}
        <FormField
          key="child-row-list-total"
          value={formattedSum}
          errorText={undefined}
          field={{ ...totalFieldBase, isMandatory: false }}
          displayOnly
          isLabelHidden={filled.length === 0 ? false : true}
          width={totalFieldWidth}
        />
      </div>
    );
  }

  // ─── Editable render ─────────────────────────────────────────────────────────

  return (
    <Box sx={{ width: '100%' }}>
      {!isLabelHidden ? (
        <Typography
          component="div"
          sx={{
            color: colors.primary,
            fontWeight: 600,
            mb: 1,
            maxWidth: '100%',
          }}
          variant="subtitle1"
        >
          {field.text}
          <RequiredMark show={field.isMandatory} />
        </Typography>
      ) : null}

      <Box sx={{ ...tableFieldOutlineSx, width: '100%' }}>
        {rows.map((row, rowIndex) => (
          <ChildRow
            key={row.id}
            row={row}
            rowIndex={rowIndex}
            field={field}
            rows={rows}
            valueFieldTemplate={valueFieldTemplate}
            formStateForVisibility={formStateForVisibility}
            onSelect={handleSelectChange}
            onValueChange={handleValueChange}
            onOtherDescriptionChange={handleOtherDescriptionChange}
            onNeighborComplaintChange={handleNeighborComplaintChange}
            onCodeIssueChange={handleCodeIssueChange}
            onPreservationOrCosmeticChange={handlePreservationOrCosmeticChange}
            onEmergencyChange={handleEmergencyChange}
            onPicturesAttachedChange={handlePicturesAttachedChange}
            onDelete={handleDeleteRow}
          />
        ))}

        <Box
          sx={{
            backgroundColor: '#f9fbfd',
            border: formUi.card.border,
            borderRadius: formUi.card.radius,
            px: 1.75,
            py: 1.75,
          }}
        >
          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              flexWrap: 'wrap',
              gap: 2,
              justifyContent: 'flex-end',
            }}
          >
            <Typography
              component="span"
              sx={{
                color: colors.text.primary,
                fontSize: '1rem',
                fontWeight: 700,
              }}
              variant="body1"
            >
              Total
              <RequiredMark show={field.isMandatory} />
            </Typography>
            <FormField
              key="child-row-list-total"
              value={formattedSum}
              errorText={errorText}
              field={totalFieldBase}
              displayOnly
              isLabelHidden
              width={totalFieldWidth}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ChildRowList;
